import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getRandomWord,
  getRandomWordWithDefinition,
  getWordDefinition,
  createWordArray,
  checkLetter,
  checkWin,
  getDisplayWord,
  MAX_ATTEMPTS,
  getKeyboardLayout
} from '../utils/gameUtils2';
import { joinGame, sendGameUpdate, onPlayerJoined, onGameUpdate } from '../utils/socketUtils';
import Keyboard from './Keyboard';
import HangmanDisplay from './HangmanDisplay';
import OnlineUsers from './OnlineUsers';
import countries from '../utils/countryData';
import { 
  getCurrentUser, 
  updateUserStats, 
  getTopUsers, 
  getUserRank,
  getAllCountries
} from '../utils/storageUtils';
import '../styles/Game.css';

const Game = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [word, setWord] = useState('');
  const [wordArray, setWordArray] = useState([]);
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost'
  const [message, setMessage] = useState('');
  const [showHighscores, setShowHighscores] = useState(false);
  const [highscores, setHighscores] = useState([]);
  const [filteredHighscores, setFilteredHighscores] = useState([]);
  const [scoreFilter, setScoreFilter] = useState('global'); // 'global', 'country'
  const [userRankGlobal, setUserRankGlobal] = useState(null);
  const [userRankCountry, setUserRankCountry] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [showOnScreenKeyboard, setShowOnScreenKeyboard] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(100); // 100 seconds
  const [showDefinition, setShowDefinition] = useState(false);
  const [currentWordData, setCurrentWordData] = useState(null);
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Points per win
  const POINTS_PER_WIN = 10;

  // Initialize game
  useEffect(() => {
    // Get user data from localStorage
    const userData = getCurrentUser();
    
    if (!userData) {
      navigate('/');
      return;
    }
    
    setUser(userData);
    loadHighscores();
    startNewGame();
    setLoading(false);
    
    // Join the game with user data (using mock socket)
    joinGame(userData);
    
    // Set up socket event listeners
    const playerJoinedCleanup = onPlayerJoined((data) => {
      console.log(`${data.twitterUsername} from ${data.country} joined the game`);
      // You could show a notification or update UI here
    });
    
    const gameUpdateCleanup = onGameUpdate((data) => {
      console.log('Game update received:', data);
      // Handle game updates if needed
    });
    
    // Cleanup function
    return () => {
      playerJoinedCleanup();
      gameUpdateCleanup();
      // Clear timer on component unmount
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [navigate]);

  // Add event listener for pausing gameplay
  useEffect(() => {
    const handlePauseGameplay = (event) => {
      setIsPaused(event.detail.paused);
    };

    window.addEventListener('pauseGameplay', handlePauseGameplay);
    
    return () => {
      window.removeEventListener('pauseGameplay', handlePauseGameplay);
    };
  }, []);

  // Add keyboard event listener
  useEffect(() => {
    // Focus the input field to trigger the keyboard on mobile devices
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Handle keyboard events
    const handleKeyDown = (e) => {
      // Don't process keyboard input if the game is paused (user is searching)
      if (isPaused) return;
      
      // Skip if game is over or loading
      if (gameStatus !== 'playing' || loading) return;
      
      // Skip key combinations like Ctrl+R, Ctrl+C etc.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Only handle letter keys
      if (/^[a-zA-Z]$/.test(e.key) && gameStatus === 'playing') {
        handleGuess(e.key.toUpperCase());
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameStatus, loading, guessedLetters, isPaused]);

  // Update filtered highscores when filter changes
  useEffect(() => {
    if (highscores.length === 0 || !user) return;
    
    let filtered = [...highscores];
    
    if (scoreFilter === 'country') {
      if (selectedCountry) {
        filtered = highscores.filter(player => player.country === selectedCountry);
      } else {
        filtered = highscores.filter(player => player.country === user.country);
      }
    }
    
    setFilteredHighscores(filtered.slice(0, 10)); // Top 10 players
    
    // Calculate user's rank
    setUserRankGlobal(getUserRank({
      username: user.twitterUsername
    }));
    
    setUserRankCountry(getUserRank({
      username: user.twitterUsername,
      countryFilter: user.country
    }));
    
  }, [scoreFilter, highscores, user, selectedCountry]);

  // Add timer effect
  useEffect(() => {
    // Only run timer when game is playing and not paused
    if (gameStatus === 'playing' && !isPaused) {
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Set up new timer
      timerRef.current = setInterval(() => {
        setTimeRemaining(prevTime => {
          if (prevTime <= 1) {
            // Time's up - end the game
            clearInterval(timerRef.current);
            setGameStatus('lost');
            setMessage(`Time's up! The word was: ${word.toUpperCase()}`);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else if (gameStatus !== 'playing' || isPaused) {
      // Pause timer when game is over or paused
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    
    // Cleanup timer on effect cleanup
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameStatus, isPaused, word]);

  // Load highscores
  const loadHighscores = () => {
    try {
      // Get top users
      const topUsers = getTopUsers();
      setHighscores(topUsers);
      setFilteredHighscores(topUsers.slice(0, 10)); // Top 10 players
      
      // Get available countries
      const countries = getAllCountries();
      setAvailableCountries(countries);
      
      // Set initial country filter to user's country if available
      const currentUser = getCurrentUser();
      if (currentUser && currentUser.country) {
        setSelectedCountry(currentUser.country);
      }
    } catch (error) {
      console.error('Error loading highscores:', error);
      setHighscores([]);
      setFilteredHighscores([]);
    }
  };

  // Start a new game
  const startNewGame = () => {
    // First, immediately clear the current game state
    setGameStatus('resetting');
    setMessage('');
    setGuessedLetters([]);
    setWrongGuesses(0);
    // Reset timer
    setTimeRemaining(100);
    // Hide definition if shown
    setShowDefinition(false);
    
    // Short timeout to ensure UI updates before setting new word
    setTimeout(() => {
      const wordData = getRandomWordWithDefinition();
      setWord(wordData.word);
      setCurrentWordData(wordData);
      setWordArray(createWordArray(wordData.word));
      setGameStatus('playing');
      
      // Focus the input field
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50); // Very short delay, just enough for state changes to be processed
  };

  // Handle letter guess
  const handleGuess = async (letter) => {
    // Ignore if game is over or letter already guessed
    if (gameStatus !== 'playing' || guessedLetters.includes(letter)) {
      return;
    }

    // Add letter to guessed letters
    setGuessedLetters([...guessedLetters, letter]);

    // Check if letter is in word
    const { updatedWordArray, found } = checkLetter(letter, wordArray);
    setWordArray(updatedWordArray);

    if (!found) {
      // Increment wrong guesses
      const newWrongGuesses = wrongGuesses + 1;
      setWrongGuesses(newWrongGuesses);

      // Check if game is lost
      if (newWrongGuesses >= MAX_ATTEMPTS) {
        setGameStatus('lost');
        setMessage(`Game Over! The word was: ${word.toUpperCase()}`);
        
        // Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        
        // Update user stats for lost game
        if (user) {
          const updatedUser = updateUserStats('lost');
          setUser(updatedUser);
          
          // Update highscores
          loadHighscores();
          
          // Send game update to server
          sendGameUpdate({
            userId: user.twitterUsername,
            result: 'lost',
            word: word,
            points: 0
          });
        }
      }
    } else {
      // Check if game is won
      if (checkWin(updatedWordArray)) {
        setGameStatus('won');
        setMessage(`Congratulations! You guessed the word: ${word.toUpperCase()}`);
        
        // Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        
        // Update user stats
        if (user) {
          const updatedUser = updateUserStats('won');
          setUser(updatedUser);
          
          // Update highscores
          loadHighscores();
          
          // Send game update to server
          sendGameUpdate({
            userId: user.twitterUsername,
            result: 'won',
            word: word,
            points: POINTS_PER_WIN
          });
        }
      }
    }
  };

  // Toggle highscores display
  const toggleHighscores = () => {
    setShowHighscores(!showHighscores);
  };

  // Handle filter change
  const handleFilterChange = (filter) => {
    setScoreFilter(filter);
  };

  // Handle country selection change
  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('hangmanUser');
    navigate('/');
  };

  // Toggle on-screen keyboard
  const toggleOnScreenKeyboard = () => {
    setShowOnScreenKeyboard(!showOnScreenKeyboard);
    
    // Only focus the input field when turning off the on-screen keyboard
    if (showOnScreenKeyboard && inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle input change (for physical keyboard)
  const handleInputChange = (e) => {
    // Only process input when on-screen keyboard is not shown
    if (!showOnScreenKeyboard) {
      const value = e.target.value;
      // Get the last character entered
      if (value.length > 0) {
        const lastChar = value.charAt(value.length - 1).toUpperCase();
        if (/^[A-Z]$/.test(lastChar)) {
          handleGuess(lastChar);
        }
        // Clear the input after processing
        e.target.value = '';
      }
    }
  };

  // Get flag emoji for country name
  const getCountryFlag = (countryName) => {
    const country = countries.find(c => c.name === countryName);
    return country ? country.flag : '🏳️';
  };

  // Toggle word definition display
  const toggleDefinition = () => {
    setShowDefinition(!showDefinition);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="game-container container">
      <div className="game-header">
        <h1 className="page-title">Hangman Game</h1>
        <div className="user-info">
          <p>
            <span className="user-label">Player:</span> 
            <span className="user-value">@{user.twitterUsername}</span>
          </p>
          <p>
            <span className="user-label">Country:</span> 
            <span className="user-value" title={user.country}>
              {getCountryFlag(user.country)}
            </span>
          </p>
          <p>
            <span className="user-label">Score:</span> 
            <span className="user-value">{user.score || 0}</span>
          </p>
          {user.gamesPlayed > 0 && (
            <p>
              <span className="user-label">Games:</span> 
              <span className="user-value">{user.gamesWon} / {user.gamesPlayed}</span>
            </p>
          )}
        </div>
      </div>

      {/* Add timer display */}
      <div className="timer-container">
        <div className={`timer ${timeRemaining <= 20 ? 'timer-warning' : ''}`}>
          Time: {timeRemaining} seconds
        </div>
      </div>

      {showHighscores ? (
        <div className="highscores-container card">
          <h2 className="highscores-title">Top Players</h2>
          
          <div className="highscores-filters">
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${scoreFilter === 'global' ? 'active' : ''}`}
                onClick={() => handleFilterChange('global')}
              >
                Global
              </button>
              <button 
                className={`filter-btn ${scoreFilter === 'country' ? 'active' : ''}`}
                onClick={() => handleFilterChange('country')}
              >
                By Country
              </button>
            </div>
            
            {scoreFilter === 'country' && (
              <div className="country-selector">
                <select 
                  value={selectedCountry} 
                  onChange={handleCountryChange}
                  className="country-select"
                >
                  <option value={user.country}>
                    {getCountryFlag(user.country)} Your Country
                  </option>
                  {availableCountries.map(country => (
                    country !== user.country && (
                      <option key={country} value={country}>
                        {getCountryFlag(country)} {country}
                      </option>
                    )
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {user.gamesPlayed > 0 && (
            <div className="user-ranking">
              <p>
                Your Rank: 
                {scoreFilter === 'global' 
                  ? ` #${userRankGlobal} Globally` 
                  : ` #${userRankCountry} in ${getCountryFlag(selectedCountry || user.country)}`}
              </p>
            </div>
          )}
          
          {filteredHighscores.length === 0 ? (
            <p className="no-highscores">No highscores available yet.</p>
          ) : (
            <div className="highscores-list">
              <div className="highscores-header">
                <span className="rank">#</span>
                <span className="player">Player</span>
                <span className="country">Flag</span>
                <span className="score">Score</span>
              </div>
              
              {filteredHighscores.map((player, index) => {
                const isCurrentUser = player.twitterUsername === user.twitterUsername;
                const countryName = player.country || '';
                const countryData = countries.find(c => c.name === countryName);
                const flagEmoji = countryData ? countryData.flag : '';
                
                return (
                  <div 
                    key={player.twitterUsername} 
                    className={`highscore-item ${isCurrentUser ? 'current-user' : ''}`}
                  >
                    <span className="rank">{index + 1}</span>
                    <span className="player">@{player.twitterUsername}</span>
                    <span className="country">
                      {flagEmoji && (
                        <span 
                          className="country-flag"
                          title={countryName}
                        >
                          {flagEmoji}
                        </span>
                      )}
                    </span>
                    <span className="score">{player.score || 0}</span>
                  </div>
                );
              })}
            </div>
          )}
          
          <button onClick={toggleHighscores} className="back-to-game-btn">
            Back to Game
          </button>
        </div>
      ) : (
        <div className="game-content card">
          <HangmanDisplay wrongGuesses={wrongGuesses} />
          
          <div className="word-display">
            <p>{getDisplayWord(wordArray)}</p>
          </div>
          
          {message && (
            <div className={`game-message ${gameStatus === 'won' ? 'success-message' : 'error-message'}`}>
              {message}
              
              {(gameStatus === 'won' || gameStatus === 'lost') && (
                <button onClick={toggleDefinition} className="definition-btn">
                  {showDefinition ? 'Hide Definition' : 'Show Definition'}
                </button>
              )}
            </div>
          )}
          
          {showDefinition && currentWordData && (
            <div className="definition-modal">
              <div className="definition-content">
                <h3>{currentWordData.word}</h3>
                <p><strong>Meaning:</strong> {currentWordData.meaning}</p>
                <p><strong>Example:</strong> <em>{currentWordData.example}</em></p>
                <button onClick={toggleDefinition} className="close-definition-btn">
                  Close
                </button>
              </div>
            </div>
          )}
          
          <div className="game-controls">
            {gameStatus === 'playing' ? (
              <div className="attempts-info">
                <p>Wrong Attempts: {wrongGuesses} / {MAX_ATTEMPTS}</p>
              </div>
            ) : gameStatus === 'resetting' ? (
              <button className="new-game-btn loading-btn">
                Loading...
              </button>
            ) : (
              <button onClick={startNewGame} className="new-game-btn">
                Play Again
              </button>
            )}
          </div>
          
          <div className="keyboard-controls">
            <button 
              className="toggle-keyboard-btn" 
              onClick={toggleOnScreenKeyboard}
            >
              {showOnScreenKeyboard ? 'Hide Keyboard' : 'Show Keyboard'}
            </button>
            
            {/* Hidden input to capture physical keyboard input */}
            <input
              ref={inputRef}
              type="text"
              className="keyboard-input"
              onChange={handleInputChange}
              autoFocus={!showOnScreenKeyboard}
              readOnly={showOnScreenKeyboard}
              aria-label="Type letters here"
            />
            
            <p className="keyboard-instructions">
              {showOnScreenKeyboard ? 
                'Click letters or use your keyboard to play' : 
                'Use your keyboard to type letters or tap here for mobile keyboard'}
            </p>
          </div>
          
          {showOnScreenKeyboard && (
            <Keyboard 
              guessedLetters={guessedLetters}
              onLetterClick={handleGuess}
              disabled={gameStatus !== 'playing'}
            />
          )}
        </div>
      )}
      
      <div className="game-footer">
        <button onClick={toggleHighscores} className="highscores-btn">
          {showHighscores ? 'Back to Game' : 'Highscores'}
        </button>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </div>
      
      {/* Add the OnlineUsers component */}
      <OnlineUsers />
    </div>
  );
};

export default Game; 